import type { AuthUser, OnboardingStatus } from "./types";

export const SELLER_ONBOARDING_PATH = "/onboarding/seller";
export const BUYER_ONBOARDING_PATH = "/onboarding/buyer-preferences";

export const SELLER_ONBOARDING_FIELD_LABELS: Record<string, string> = {
  phone: "WhatsApp number",
  // Distinct from `phone` above (which is the OTP/login number, also labelled
  // "WhatsApp number") — kept disambiguated so a missing-fields list that
  // contains both doesn't show the same label twice.
  whatsapp_number: "WhatsApp contact number",
  agency_name: "Agency name",
  seller_types: "Seller types",
  country: "Country",
  city: "City",
};

export function isSellerRole(role: string | null | undefined): boolean {
  return role === "seller" || role === "super_admin";
}

export function isBuyerRole(role: string | null | undefined): boolean {
  return role === "buyer" || role === "user" || role === "super_admin";
}

export function getOnboardingStatus(user: AuthUser | null | undefined): OnboardingStatus | null {
  return user?.onboarding ?? null;
}

export function getMissingSellerOnboardingFields(
  user: AuthUser | null | undefined,
): string[] {
  if (!isSellerRole(user?.role)) return [];
  return user?.onboarding?.missing_fields ?? [];
}

/** Same as getMissingSellerOnboardingFields, but with each field's own
 * server-supplied label (from onboarding.missing_steps_detailed) — prefer
 * this for display; formatOnboardingFieldLabel below only knows a handful
 * of legacy field names and falls back to the raw key otherwise. */
export function getMissingSellerOnboardingFieldLabels(
  user: AuthUser | null | undefined,
): string[] {
  if (!isSellerRole(user?.role)) return [];
  const detailed = user?.onboarding?.missing_steps_detailed;
  if (detailed?.length) return detailed.map((f) => f.label);
  return (user?.onboarding?.missing_fields ?? []).map(formatOnboardingFieldLabel);
}

export function isSellerOnboardingComplete(
  user: AuthUser | null | undefined,
): boolean {
  if (user?.role === "super_admin") return true;
  if (!isSellerRole(user?.role)) return true;
  return user?.onboarding?.completed === true;
}

export function isBuyerOnboardingComplete(
  user: AuthUser | null | undefined,
): boolean {
  if (user?.role === "super_admin") return true;
  if (!isBuyerRole(user?.role)) return true;
  return user?.onboarding?.completed === true;
}

export function formatOnboardingFieldLabel(field: string): string {
  return SELLER_ONBOARDING_FIELD_LABELS[field] ?? field.replace(/_/g, " ");
}

export function getDefaultSellerDestination(): string {
  return "/inventory/my";
}
